import {
	type SponsorOptions,
	type TakeoverTemplateKey,
	type TextHorizontal,
	type TextVertical,
	type WipeDirection,
} from "./sponsorBooking";
import {offersOption, SPONSOR_OPTION_KEYS, type SponsorOptionKey} from "./sponsorOptions";
import {focalPointPercent, isCentredFocalPoint, isFocalPoint} from "./options/mediaFit";
import {chosenTextColor, posterComposition} from "./takeover/posterLayout";

/**
 * A wallpaper booking's settings, SAID IN WORDS (kitecorp/transferify-ui#1870). Plain TypeScript — this
 * module imports NO React — so "which rows does this treatment show, and what does each one say" is one
 * tested function rather than a list rebuilt beside every surface that has to show it.
 *
 * Two surfaces show exactly this, for opposite reasons. The booking page's review step asks the
 * advertiser to confirm what they chose before they pay; the operator queue at `/account/admin` asks a
 * reviewer to approve what an advertiser bought. Both must read the same words off the same options, or
 * an operator approves something described differently from the thing that was sold — and the enum names
 * (`RIGHT_LEFT`, `BOTTOM_UP`) are the wire, never copy, so neither surface may show them raw.
 *
 * The rows were built inline in `SponsorPage` until this ticket. Rebuilding them for the queue would
 * have been a second copy of the "unset is a real answer" rule below, and the copy would have been the
 * one an operator saw.
 *
 * SETTINGS ONLY. The advertiser's uploaded FILES are not here, and the reason is that the two surfaces
 * want genuinely different things from them: the booking review lists them as words ("Logo: Added"),
 * because an advertiser is confirming what they attached, while the operator queue renders the files
 * themselves — and a caption saying "Added" beside the picture says less than the picture. A shared
 * function for that would have been generality nothing asked for.
 *
 * Everything here takes its WORDS as an argument and returns none of its own, exactly as
 * `sponsorBooking.ts` returns i18n keys: a rule that answered in English would have to be rewritten to
 * add Romanian.
 */

/** The words a value row is spoken with: one translated string per enum constant. */
export interface SponsorOptionValueWords {
	direction: {values: Record<WipeDirection, string>};
	placement: {
		/** Joins the two axes into one phrase — the catalog owns the order and the punctuation. */
		cell: (vertical: string, horizontal: string) => string;
		vertical: Record<TextVertical, string>;
		horizontal: Record<TextHorizontal, string>;
	};
	/**
	 * The crop, which has no enum to name (kitecorp/transferify-ui#2143): it is two numbers, so the
	 * catalog owns both the word for the default and the punctuation the pair is read with.
	 */
	focalPoint: {
		/** What an untouched crop is called. A real answer, not an empty cell - see {@link optionRow}. */
		centre: string;
		/** The two percentages as one phrase, horizontal first, exactly as CSS orders them. */
		at: (x: number, y: number) => string;
	};
}

/** The label of each row, plus the two words that stand for a value nobody set and a file that landed. */
export interface SponsorSummaryTermWords {
	direction: string;
	placement: string;
	/** The row for `brandColor`, which is the colour of the words (kitecorp/transferify-ui#2416). */
	textColor: string;
	textBackground: string;
	subline: string;
	focalPoint: string;
	/** An option the advertiser left alone, said as a choice rather than as an empty cell. */
	unset: string;
}

/**
 * Both halves at once.
 *
 * Structural rather than a named slice of the catalog, so `sponsor.summary` and `sponsor.options` satisfy
 * it as they stand and a test can pass literal words and assert literal rows.
 */
export interface SponsorOptionSummaryWords {
	terms: SponsorSummaryTermWords;
	values: SponsorOptionValueWords;
}

export interface SponsorSummaryRow {
	/** The option's own wire name, so a view can key a list by it. */
	key: SponsorOptionKey;
	term: string;
	value: string;
	/**
	 * `#rrggbb` when this row IS one of the two colours and one was chosen, else `null`.
	 *
	 * Carried BESIDE the value rather than instead of it, because the two surfaces show the colour
	 * differently and both are right: the booking review prints the hex, and the operator queue draws a
	 * swatch beside it — a reviewer approving a colour has to see the colour. A row that carried only the
	 * string would make the swatch a second parse of it.
	 */
	swatch: string | null;
}

/**
 * The option rows for `template`, in {@link SPONSOR_OPTION_KEYS} order.
 *
 * Driven ENTIRELY by {@link allowsOption}, with no category test of its own — which is also what makes a
 * BACKDROP list nothing, because the option table gives every backdrop an empty key set. A category
 * guard beside the loop was written first and could not fail: the two would have had to disagree for it
 * to do anything, and if they ever did, the table is the half both the controls and the validator read.
 * The same table is why the wipe is the only treatment that lists a sweep direction.
 *
 * Every option the treatment takes gets a row EVEN WHEN IT IS UNSET, because "not set" is a real answer
 * with a visible result on the poster — a missing row would read as an omission from the review rather
 * than as a choice.
 */
export function sponsorOptionSummary(
	template: TakeoverTemplateKey,
	options: SponsorOptions,
	words: SponsorOptionSummaryWords,
): readonly SponsorSummaryRow[] {
	const rows: SponsorSummaryRow[] = [];
	for (const key of SPONSOR_OPTION_KEYS) {
		// What a surface may OFFER, not what the treatment takes: a review that read back a setting the
		// request is about to discard would have an advertiser confirm something nobody buys. See
		// `offersOption`, which is the one place that difference lives.
		if (offersOption(template, key)) {
			rows.push(optionRow(key, options, words));
		}
	}
	return rows;
}

/** One option, in the words its own enum is spoken with. */
function optionRow(
	key: SponsorOptionKey, options: SponsorOptions, words: SponsorOptionSummaryWords,
): SponsorSummaryRow {
	const {terms, values} = words;
	switch (key) {
		case "direction":
			return {
				key,
				term: terms.direction,
				value: options.direction === undefined
					? terms.unset
					: values.direction.values[options.direction],
				swatch: null,
			};
		case "textPlacement": {
			const placement = options.textPlacement;
			return {
				key,
				term: terms.placement,
				value: placement === undefined
					? terms.unset
					: values.placement.cell(
						values.placement.vertical[placement.vertical],
						values.placement.horizontal[placement.horizontal]),
				swatch: null,
			};
		}
		case "brandColor": {
			/*
			 * Judged exactly as the RENDERER judges it, by the renderer's own `chosenTextColor`, so the row
			 * cannot state a fact the composition above it contradicts. A value that is not `#rrggbb` is
			 * painted as the fallback ink rather than as a colour, and CSS silently drops it from a swatch
			 * — so reading it back verbatim would show an operator a hex beside an empty square. A booking
			 * stored with the retired tone reads back as the ink that tone paints, rather than as "not set"
			 * beside words that plainly have a colour.
			 */
			const textColor = chosenTextColor(options);
			return {
				key,
				term: terms.textColor,
				value: textColor ?? terms.unset,
				swatch: textColor,
			};
		}
		case "textBackgroundColor": {
			// The composition's own narrowed ground, for the same reason the text colour above is read
			// through the renderer: an unpaintable value is no plate on the page, so it is no colour here.
			const background = posterComposition(options).textBackground;
			return {
				key,
				term: terms.textBackground,
				value: background ?? terms.unset,
				swatch: background,
			};
		}
		case "focalPoint": {
			/*
			 * NEVER `terms.unset`, and that is the one row here that reads differently from the rest.
			 *
			 * Every other option has a renderer default the advertiser did not choose; this one has a
			 * VALUE they did - the centre is where an untouched marker sits and where the crop is
			 * actually taken. "Not set" would describe the same picture as a choice nobody made.
			 *
			 * Judged exactly as the STAGES judge it (`objectPosition` runs the same two functions), so
			 * the row cannot state a crop the composition contradicts: an out-of-range point is painted
			 * centred, so it is read back as the centre rather than as the numbers nobody will see.
			 */
			const focalPoint = options.focalPoint;
			return {
				key,
				term: terms.focalPoint,
				value: isCentredFocalPoint(focalPoint) || !isFocalPoint(focalPoint)
					? values.focalPoint.centre
					: percentPhrase(focalPoint, values.focalPoint.at),
				swatch: null,
			};
		}
		case "subline": {
			// Blank and absent are ONE answer here, exactly as `normalizeSponsorOptions` decides for the
			// wire: a subline of spaces stores nothing and typesets nothing, so it must not read back as
			// a value the advertiser chose.
			const subline = options.subline?.trim();
			return {
				key,
				term: terms.subline,
				value: subline === undefined || subline === "" ? terms.unset : subline,
				swatch: null,
			};
		}
	}
}

/** The two fractions as the catalog's phrase. Split out so the row above stays one expression. */
function percentPhrase(
	point: NonNullable<SponsorOptions["focalPoint"]>,
	at: (x: number, y: number) => string,
): string {
	const {x, y} = focalPointPercent(point);
	return at(x, y);
}
